// @ts-nocheck
import { XGInput, XGRadio } from 'xg-cl';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  updateActiveStep,
  updateTotalItem,
  updateBookingResult,
  updatePassengerForms,
  updatePaymentOption
} from '../../../../redux/slices/booking/booking';
import { ErrorToast } from '../../../../utils/alerts';
import {
  getBookingTotals,
  calculateAmountToCharge
} from '../../../../utils/booking';
import { initGA, trackEvent } from '../../../../utils/analytics';
import { getBookingPaymentPlan } from '../../../../utils/paymentScheduleAdapter';
import { format } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { formatDate, toUTCNoonDate } from '../../../../utils/dateUtility';
import { formatCurrency } from '../../../../utils/currencyHelper';
import {
  getTenantSettings,
  getTripSettings
} from '../../../../services/tenant/tenant';
import { getSettingMarkdownValue } from '../../../../utils/markdownHelper';
import { useSelector, useDispatch } from 'react-redux';
import { updatePaymentIntent } from '../../../../services/stripe/stripe';
import {
  completeBooking,
  uploadCheckoutPassengerFieldFile
} from '../../../../services/booking/booking';
import { getFeesBreakdown } from '../../../../utils/feesHelper';
import {
  getCheckoutAddOns,
  getPassengerTripAddOnIds,
  getSelectedAddOnsTotal,
  matchesId
} from '../../../../utils/percentageAddOns';
import Editor from '../../../Editor/Editor';
import {
  paymentElementOptions,
  requiresFullBillingAddress,
  successfulStatuses,
  SUCCEEDED
} from '../../../../constants/stripe';
import { RADIOBUTTON, BOOLEAN } from '../../../../constants/passengers';
import {
  getPassengerFieldUploadFiles,
  getPassengerFieldUploadMetadata,
  isFileUploadPassengerFieldType
} from '../../../../utils/passengerFieldTypes';
import {
  applyCheckoutUploadTokens,
  stageCheckoutPassengerUploads
} from '../../../../utils/checkoutPassengerUploads';
import {
  canMutatePaymentIntent,
  completeCapturedBooking
} from '../../../../utils/checkoutPaymentLifecycle';
import { buildBookingTermsAcceptance } from '../../../../utils/bookingTermsAcceptance';
import { sanitizeStripeReturnUrl } from '../../../../utils/stripeReturnUrl';
import {
  buildFxTransactionFields,
  hasFxPaymentSelection
} from '../../../../utils/fxTransactionFields';
import {
  useStripe,
  useElements,
  PaymentElement,
  AddressElement
} from '@stripe/react-stripe-js';
import PaymentStatusIcon from '../../../Common/PaymentStatusIcon';
import {
  setCustomForm,
  setCustomFormError,
  setIsSubmitting,
  setIsProcessing,
  setIsPaymentMethodComplete,
  resetCheckout
} from '../../../../redux/slices/checkout/checkout';
import * as PropTypes from 'prop-types';
import './CheckoutForm.css';

const PAYMENT_INSTRUCTIONS_SETTINGCODE = 'PAYMENTINSTRUCTIONS';

const isBrowserFile = (file) =>
  typeof File !== 'undefined' && file instanceof File;

const getDroppedDiscountMessages = (t, droppedDiscountCodes = []) =>
  droppedDiscountCodes.map((droppedDiscount) => {
    const code = droppedDiscount.code || '';

    if (droppedDiscount.reason === 'CODE_EXPIRED') {
      return t('trip:discountCodeExpiredBeforeBookingCompleted', { code });
    }

    return t('trip:discountCodeRemovedBeforeBookingCompleted', { code });
  });

/**
 * Functional React component for rendering a custom checkout form.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the checkout form.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the custom checkout form component.
 */
const CheckoutForm = ({
  id,
  paymentIntentId,
  paymentMethodOrder,
  hasClientSecret,
  selectedCurrency,
  currencyState,
  onPaymentMethodChange,
  onPaymentRecoveryChange,
  passengerFieldFiles = {},
  submitRef,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation([
    'trip',
    'common',
    'errors',
    'bookings',
    'settings'
  ]);
  const stripe = useStripe();
  const elements = useElements();
  const {
    enableNewPaymentPlans,
    enablePaymentInstructionsForNoPaymentBookings,
    enablePercentageBasedAddons,
    enableMultiCurrency
  } = useFlags();
  const enablePaymentInstructionsFeature = Boolean(
    enablePaymentInstructionsForNoPaymentBookings
  );
  const checkout = useSelector((state) => state.checkout) || {};
  const {
    signedDocumentId,
    requiresSignedDocument,
    customForm = { email: '', firstName: '', lastName: '' },
    customFormError = { email: '', firstName: '', lastName: '' },
    isPaymentMethodComplete = false,
    isTermsChecked = false
  } = checkout;
  const [previousPaymentMethod, setPreviousPaymentMethod] = useState(null);
  const paymentMethodSelectionRef = useRef(null);
  const paymentMethodUpdateCountRef = useRef(0);
  // Use the displayed default before Stripe's first change event so the
  // summary and intent retain the same card fees on initial payment-step load.
  const paymentMethodType =
    previousPaymentMethod?.type ??
    (enableMultiCurrency &&
    paymentMethodOrder?.includes('card') &&
    paymentMethodOrder.includes('us_bank_account')
      ? paymentMethodOrder[0]
      : '');
  const preparedPassengersRef = useRef(null);
  const capturedPaymentRef = useRef(null);
  const submissionInFlightRef = useRef(false);
  const paymentFinalizingRef = useRef(false);
  const paymentIntentUpdatePromiseRef = useRef(Promise.resolve());
  const isMountedRef = useRef(true);
  const booking = useSelector((state) => state.booking);
  const trip = booking?.trip || null;
  const operatorCurrency =
    booking?.tenant?.sellingcurrency?.currencycode ||
    booking?.tenant?.basecurrency?.currencycode;
  const hasFxSelection = hasFxPaymentSelection(
    selectedCurrency,
    operatorCurrency
  );
  const usesManagedIntent = hasFxSelection || Boolean(currencyState?.managed);
  // Memoize bookingplans to keep a stable reference across renders
  const bookingplans = useMemo(
    () => booking?.bookingplans || [],
    [booking?.bookingplans]
  );
  const passengerForms = useMemo(
    () => booking?.passengerForms || [],
    [booking?.passengerForms]
  );

  // Get pricing plan totals (does NOT include add-ons)
  const { totalPrice: totalPlansPrice, totalDeposit } = getBookingTotals(
    bookingplans,
    enableNewPaymentPlans
  );

  // Memoize addons and selectedAddOns to maintain stable references
  const addons = useMemo(() => booking?.addons || [], [booking?.addons]);
  const selectedAddOns = useMemo(
    () => booking?.selectedAddOns || [],
    [booking?.selectedAddOns]
  );
  const appliedDiscountCodes = useMemo(
    () => booking?.appliedDiscountCodes || [],
    [booking?.appliedDiscountCodes]
  );

  // Calculate add-ons total
  const totalAddOnsPrice = getSelectedAddOnsTotal({
    selectedAddOns,
    addons,
    passengers: passengerForms,
    bookingPlans: bookingplans,
    enablePercentageBasedAddons
  });

  // Total price including both plans and add-ons
  const totalPrice = totalPlansPrice + totalAddOnsPrice;
  const totalDiscountAmount = appliedDiscountCodes.reduce(
    (total, discountCode) => total + Number(discountCode?.discountAmount || 0),
    0
  );
  const discountedTotalPrice = Math.max(totalPrice - totalDiscountAmount, 0);
  const isZeroTotalBooking = Number(discountedTotalPrice || 0) <= 0;

  const [planCalc, setPlanCalc] = useState(null);
  const [paymentInstructionsMarkdown, setPaymentInstructionsMarkdown] =
    useState('');
  const color = useSelector((state) => state?.color || {});
  const primaryColor = color?.highlightColor || 'var(--primary-600, #1361d8)';

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updatePaymentIntentWhileMutable = useCallback(
    (intentId, payload) => {
      const updatePromise = paymentIntentUpdatePromiseRef.current
        .catch(() => {})
        .then(() => {
          if (
            !intentId ||
            ((hasFxSelection || currencyState?.managed) &&
              payload.amount != null) ||
            !canMutatePaymentIntent({
              capturedPayment: capturedPaymentRef.current,
              paymentFinalizing: paymentFinalizingRef.current
            })
          ) {
            return { skipped: true };
          }

          return updatePaymentIntent(intentId, payload);
        });

      paymentIntentUpdatePromiseRef.current = updatePromise;
      return updatePromise;
    },
    [hasFxSelection, currencyState?.managed]
  );

  const displayDeposit = useMemo(() => {
    if (enableNewPaymentPlans) {
      const dueNow = Number(planCalc?.dueAtBooking ?? 0);
      const total = Number(planCalc?.totalPrice ?? 0);
      return (
        planCalc && !planCalc.mustPayInFull && dueNow > 0 && dueNow < total
      );
    }
    return totalDeposit >= 1;
  }, [enableNewPaymentPlans, planCalc, totalDeposit]);

  const dateFormat = t('common:dateFormat');

  const allowBookingWithoutPayment =
    trip?.paymentplan?.allowbookingwithoutpayment ?? false;

  // Booking date reference (YYYY-MM-DD) for schedule comparisons
  const bookingDateYMD = useMemo(
    () => formatDate(new Date().toISOString(), 'YYYY-MM-DD'),
    []
  );

  // Check if there are interim payments (second/third/fourth) in the schedule
  const hasInterimPayments = useMemo(() => {
    if (!enableNewPaymentPlans) return false;
    if (!planCalc?.allEntries || !Array.isArray(planCalc.allEntries))
      return false;

    return planCalc.allEntries.some(
      (entry) =>
        entry.label === 'second' ||
        entry.label === 'third' ||
        entry.label === 'fourth'
    );
  }, [enableNewPaymentPlans, planCalc]);

  // Build the displayable schedule breakdown for Deposit + Installments
  const depositBreakdown = useMemo(() => {
    if (!enableNewPaymentPlans) return [];
    if (!planCalc?.allEntries || !Array.isArray(planCalc.allEntries)) return [];

    const entries = planCalc.allEntries;
    const result = [];

    // Helper to format a date for UI
    const fmt = (ymd) => {
      const d = toUTCNoonDate(ymd);
      return d ? format(d, dateFormat) : ymd;
    };

    // Status types with colors and icons:
    // - completed: Green check (payment already received)
    // - dueToday: Blue ellipsis (payment due today)
    // - overdue: Red exclamation (payment past due)
    // - upcoming: White/grey circle (future payment)

    // 1) Deposit (due at booking) if present - always "Payment Due Today"
    const depositEntry = entries.find((e) => e.label === 'deposit');
    if (depositEntry) {
      // Check if any selected add-ons are due at booking
      const hasAddOnsDueAtBooking = selectedAddOns.some((selectedAddOn) => {
        const addon = addons.find((a) =>
          matchesId(a.tripaddonid, selectedAddOn.tripaddonid)
        );
        return addon?.dueatbooking === true;
      });

      const depositLabel = hasAddOnsDueAtBooking
        ? `${t('trip:deposit') || 'Deposit'} + add-ons (due at booking)`
        : `${t('trip:deposit') || 'Deposit'} (due at booking)`;

      result.push({
        key: `deposit-${depositEntry.date}`,
        status: 'dueToday',
        amount: formatCurrency(Number(depositEntry.amount || 0)),
        text: depositLabel
      });
    }

    // 2) Payments due today or overdue (date <= booking date and not deposit) - "Payment Due Today" or "Overdue Payment"
    const dueOrOverdue = entries.filter(
      (e) => e.label !== 'deposit' && e.date && e.date <= bookingDateYMD
    );
    dueOrOverdue.forEach((e, idx) => {
      const isDueToday = e.date === bookingDateYMD;
      result.push({
        key: `${isDueToday ? 'duetoday' : 'overdue'}-${idx}-${e.date}`,
        status: isDueToday ? 'dueToday' : 'overdue',
        amount: formatCurrency(Number(e.amount || 0)),
        text: isDueToday
          ? `Payment due today`
          : `Overdue payment from ${fmt(e.date)}`
      });
    });

    // 3) Future schedule (date > booking date) - "Upcoming Payment"
    const future = Array.isArray(planCalc.schedule) ? planCalc.schedule : [];
    future.forEach((e, idx) => {
      const isFinal = String(e.label).toLowerCase() === 'final';
      result.push({
        key: `future-${idx}-${e.date}`,
        status: 'upcoming',
        amount: formatCurrency(Number(e.amount || 0)),
        text: `${isFinal ? 'Final Payment' : 'Payment'} ${fmt(e.date)}`
      });
    });

    return result;
  }, [
    enableNewPaymentPlans,
    planCalc,
    bookingDateYMD,
    dateFormat,
    t,
    addons,
    selectedAddOns
  ]);

  // Get selectedPaymentOption from Redux state instead of local state
  // Default to deposit when available, otherwise full amount
  const selectedPaymentOption = isZeroTotalBooking
    ? 'optionnopayment'
    : (booking?.paymentOption ??
      (displayDeposit ? 'optiondeposit' : 'optionfullamount'));
  const amountToCharge = booking?.amountToCharge || 0;
  const fullPaymentAmount = useMemo(() => {
    if (!enableNewPaymentPlans || planCalc?.canBook === false) {
      return discountedTotalPrice;
    }

    return Number(planCalc?.totalPrice ?? discountedTotalPrice);
  }, [discountedTotalPrice, enableNewPaymentPlans, planCalc]);
  const shouldCollectStripePayment =
    !isZeroTotalBooking && selectedPaymentOption !== 'optionnopayment';

  useEffect(() => {
    const loadPaymentInstructions = async () => {
      if (!enablePaymentInstructionsFeature) {
        setPaymentInstructionsMarkdown('');
        return;
      }

      try {
        const tripId = Number(trip?.tripid || 0);
        const response =
          tripId > 0
            ? await getTripSettings(tripId, PAYMENT_INSTRUCTIONS_SETTINGCODE)
            : await getTenantSettings(PAYMENT_INSTRUCTIONS_SETTINGCODE);
        const markdown = getSettingMarkdownValue(response?.data?.settingvalue);
        setPaymentInstructionsMarkdown(markdown);
      } catch (error) {
        setPaymentInstructionsMarkdown('');
      }
    };

    loadPaymentInstructions();
  }, [enablePaymentInstructionsFeature, trip?.tripid]);

  // Define updateBookingTotal before useEffect to avoid circular dependencies
  const updateBookingTotal = useCallback(
    (newAmount = 0, fees = [], paymentRef = '') => {
      const { totalWithFees, totalFees } = getFeesBreakdown(
        newAmount ?? 0,
        fees ?? [],
        paymentRef ?? ''
      );

      const subtotal = totalWithFees - totalFees;

      dispatch(
        updateTotalItem({
          totalKey: 'trip-total',
          totalItem: {
            priceperperson: totalWithFees,
            totalPrice: totalWithFees
          }
        })
      );

      // Show subtotal when there's a payment amount (even with zero fees)
      // Hide subtotal only for "Complete without payment" option
      const shouldShowSubtotal = newAmount > 0;

      dispatch(
        updateTotalItem({
          totalKey: 'trip-subtotal',
          totalItem: {
            priceperperson: subtotal,
            totalPrice: subtotal,
            visible: shouldShowSubtotal
          }
        })
      );

      if (totalFees > 0) {
        dispatch(
          updateTotalItem({
            totalKey: 'trip-totalfees',
            totalItem: {
              priceperperson: totalFees,
              totalPrice: totalFees,
              visible: true
            }
          })
        );
      } else {
        dispatch(
          updateTotalItem({
            totalKey: 'trip-totalfees',
            totalItem: {
              priceperperson: totalFees,
              totalPrice: totalFees,
              visible: false
            }
          })
        );
      }
    },
    [dispatch]
  );

  // Helper: map current selections to adapter input (date-only strings)
  const buildPaymentPlanInput = useCallback(() => {
    try {
      const toYMD = (val) => {
        if (!val) return null;
        // Accept YYYY-MM-DD substring when ISO present
        const s = typeof val === 'string' ? val : new Date(val).toISOString();
        return formatDate(s, 'YYYY-MM-DD');
      };

      const selectedPlans = (bookingplans || [])
        .filter((p) => Number(p?.selectedQuantity) > 0)
        .map((p) => ({
          planId: String(p?.tripplanid ?? p?.planid ?? ''),
          occupancy: Number(p?.selectedQuantity) || 0,
          pricePerPerson: Number(p?.priceperperson) || 0,
          bookingDeadline: toYMD(p?.lastbookdate) || null
        }));

      const bookingAddOns = getCheckoutAddOns({
        addons,
        selectedAddOns,
        passengers: passengerForms,
        bookingPlans: bookingplans
      });

      const schedule = trip?.paymentplan || {};
      // If no payment plan exists, default to pay in full
      const paymentPlan = {
        depositAmount: schedule.depositamount || '100%',
        secondPaymentAmount: schedule.secondpaymentamount ?? null,
        secondPaymentDue: schedule.secondpaymentdue ?? null,
        thirdPaymentAmount: schedule.thirdpaymentamount ?? null,
        thirdPaymentDue: schedule.thirdpaymentdue ?? null,
        fourthPaymentAmount: schedule.fourthpaymentamount ?? null,
        fourthPaymentDue: schedule.fourthpaymentdue ?? null,
        finalPaymentDue: schedule.finalpaymentdue ?? 0,
        enablePartialPayments: schedule.enablepartialpayments ?? false,
        allowBookingWithoutPayment: schedule.allowbookingwithoutpayment ?? false
      };

      // Use browser local date for bookingCreationDate to ensure 'today' matches user's timezone
      // This allows bookings on the deadline day until midnight (browser local time)
      const now = new Date();
      const localBookingDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      return {
        tripStartDate: toYMD(trip?.startdate),
        bookingCreationDate: localBookingDate,
        bookingPlans: selectedPlans,
        paymentPlan,
        priceAdjustment: -totalDiscountAmount,
        bookingAddOns
      };
    } catch (e) {
      console.error('Error building payment plan input:', e);
      return null;
    }
  }, [
    bookingplans,
    addons,
    selectedAddOns,
    passengerForms,
    trip?.paymentplan,
    trip?.startdate,
    totalDiscountAmount
  ]);

  // Initialize amountToCharge when component mounts or booking data changes
  useEffect(() => {
    dispatch(setIsPaymentMethodComplete(!shouldCollectStripePayment));
  }, [dispatch, shouldCollectStripePayment]);

  useEffect(() => {
    if (isZeroTotalBooking) {
      const alreadySetToNoPayment =
        booking?.paymentOption === 'optionnopayment' &&
        Number(booking?.amountToCharge ?? 0) === 0;

      if (!alreadySetToNoPayment) {
        updateBookingTotal(0, [], '');
        dispatch(
          updatePaymentOption({
            paymentOption: 'optionnopayment',
            amountToCharge: 0
          })
        );
      }
      return;
    }

    let initialAmount = 0;
    let shouldShowDeposit = false; // Will be calculated based on payment plan result
    let optionToUse = selectedPaymentOption; // Default to current selected option

    if (enableNewPaymentPlans) {
      const input = buildPaymentPlanInput();
      if (input) {
        const result = getBookingPaymentPlan({
          ...input,
          featureFlags: { enableNewPaymentPlans }
        });
        setPlanCalc(result);

        if (result?.canBook === false) {
          updateBookingTotal(0, [], '');
          dispatch(
            updatePaymentOption({
              paymentOption: 'optionfullamount',
              amountToCharge: 0
            })
          );
          return;
        }

        // Determine if deposit option should be available based on the payment plan result
        const dueNow = Number(result?.dueAtBooking ?? 0);
        const total = Number(result?.totalPrice ?? 0);
        shouldShowDeposit =
          !result.mustPayInFull && dueNow > 0 && dueNow < total;

        // Determine the payment option to use
        // If booking.paymentOption is null (first time), default to deposit if available
        optionToUse =
          booking.paymentOption === null
            ? shouldShowDeposit
              ? 'optiondeposit'
              : 'optionfullamount'
            : selectedPaymentOption;

        // Calculate amount based on the determined payment option
        if (optionToUse === 'optionnopayment') {
          initialAmount = 0;
        } else if (optionToUse === 'optionfullamount') {
          initialAmount = Number(result?.totalPrice ?? discountedTotalPrice);
        } else {
          initialAmount = Number(result?.dueAtBooking ?? 0);
        }

        // Check if must pay in full (unless booking without payment is allowed and selected)
        if (
          result?.mustPayInFull &&
          !(allowBookingWithoutPayment && optionToUse === 'optionnopayment')
        ) {
          // Override to full amount when payment in full is required
          initialAmount = Number(result?.totalPrice ?? discountedTotalPrice);
          const feesArg = trip?.fees ?? [];
          const paymentRefArg = paymentMethodType;
          updateBookingTotal(initialAmount, feesArg, paymentRefArg);

          dispatch(
            updatePaymentOption({
              paymentOption: 'optionfullamount',
              amountToCharge: initialAmount
            })
          );
          return; // Exit early to avoid duplicate dispatch
        }
      }
    } else {
      // Legacy payment plan logic
      initialAmount = calculateAmountToCharge(
        selectedPaymentOption,
        discountedTotalPrice,
        totalDeposit
      );
    }

    // Determine the correct payment option to dispatch
    const correctPaymentOption =
      booking.paymentOption === null
        ? shouldShowDeposit
          ? 'optiondeposit'
          : 'optionfullamount'
        : optionToUse;

    // Always update the booking totals to reflect the current payment option
    const feesArg =
      correctPaymentOption === 'optionnopayment' ? [] : (trip?.fees ?? []);
    const paymentRefArg =
      correctPaymentOption === 'optionnopayment' ? '' : paymentMethodType;

    updateBookingTotal(initialAmount ?? 0, feesArg, paymentRefArg);

    // Only dispatch Redux update if the amount has actually changed or payment option needs to be set
    if (
      amountToCharge === null ||
      amountToCharge !== initialAmount ||
      booking.paymentOption === null
    ) {
      dispatch(
        updatePaymentOption({
          paymentOption: correctPaymentOption,
          amountToCharge: initialAmount
        })
      );

      // CRITICAL: Update the payment intent with the correct amount
      // The payment intent was created in BookingModal, we need to update it to match
      // the selected payment option (deposit vs full amount)
      if (
        correctPaymentOption !== 'optionnopayment' &&
        paymentIntentId &&
        stripe &&
        elements
      ) {
        const {
          totalWithFeesInChargeCurrency,
          applicationFeeNotIncluded,
          applicationFeeIncluded
        } = getFeesBreakdown(
          initialAmount ?? 0,
          trip?.fees ?? [],
          paymentMethodType
        );

        // Update payment intent asynchronously but don't block the UI
        // The last-looks check before charge will ensure correctness
        updatePaymentIntentWhileMutable(paymentIntentId, {
          amount: totalWithFeesInChargeCurrency,
          applicationFees: applicationFeeNotIncluded + applicationFeeIncluded,
          metadata: {
            'Payment type':
              correctPaymentOption === 'optionfullamount'
                ? t('trip:fullAmount')
                : t('trip:depositAmount')
          }
        }).catch((error) => {
          console.error(
            '[BOOKING] Error updating payment intent in useEffect:',
            error
          );
          // Don't show error to user - last-looks check will handle it
        });
      }
    }
  }, [
    dispatch,
    selectedPaymentOption,
    totalPrice,
    discountedTotalPrice,
    totalDeposit,
    amountToCharge,
    trip?.fees,
    paymentMethodType,
    updateBookingTotal,
    enableNewPaymentPlans,
    buildPaymentPlanInput,
    allowBookingWithoutPayment,
    // displayDeposit removed - it depends on planCalc which is set inside this effect
    // planCalc removed - it's set inside this effect, causes infinite loop
    booking.paymentOption,
    booking?.amountToCharge,
    paymentIntentId,
    stripe,
    elements,
    t,
    isZeroTotalBooking,
    updatePaymentIntentWhileMutable
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submissionInFlightRef.current) return;
    submissionInFlightRef.current = true;
    let currencyPaymentStarted = false;
    let currencyPaymentCompleted = false;
    try {
      if (paymentMethodUpdateCountRef.current > 0) return;
      // Resolve every selection before charging. An already captured payment
      // must continue booking completion without being gated by fresh pricing.
      if (!capturedPaymentRef.current) {
        getCheckoutAddOns({
          addons,
          selectedAddOns,
          passengers: passengerForms,
          bookingPlans: bookingplans
        });
        if (enableNewPaymentPlans) {
          const input = buildPaymentPlanInput();
          if (!input) throw new Error('Checkout pricing could not be resolved');
          const result = getBookingPaymentPlan({
            ...input,
            featureFlags: { enableNewPaymentPlans }
          });
          if (result?.canBook === false) {
            throw new Error('Checkout payment schedule is invalid');
          }
          const expectedAmount =
            booking.paymentOption === 'optionnopayment'
              ? 0
              : Number(
                  booking.paymentOption === 'optionfullamount'
                    ? result.totalPrice
                    : result.dueAtBooking
                );
          if (!Number.isFinite(expectedAmount)) {
            throw new Error('Checkout amount could not be resolved');
          }
          if (
            !Number.isFinite(Number(booking.amountToCharge)) ||
            Math.abs(expectedAmount - Number(booking.amountToCharge)) > 0.005
          ) {
            updateBookingTotal(
              expectedAmount,
              trip?.fees ?? [],
              paymentMethodType
            );
            dispatch(
              updatePaymentOption({
                paymentOption: booking.paymentOption,
                amountToCharge: expectedAmount
              })
            );
            throw new Error('Checkout amount changed before payment');
          }
        }
      }
      const shouldProcessPayment =
        !isZeroTotalBooking &&
        booking.paymentOption != 'optionnopayment' &&
        Number(booking.amountToCharge || 0) > 0;
      const type = paymentMethodSelectionRef.current?.type ?? paymentMethodType;

      if (shouldProcessPayment && (!stripe || !elements)) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      if (shouldProcessPayment && !isPaymentMethodComplete) {
        ErrorToast(t('errors:bookingPaymentMethodRequired'));
        return;
      }

      if (
        shouldProcessPayment &&
        !capturedPaymentRef.current &&
        currencyState
      ) {
        if (!currencyState.beginPayment(selectedCurrency)) return;
        currencyPaymentStarted = true;
      }
      if (
        !shouldProcessPayment &&
        currencyState?.managed &&
        !(await currencyState.reset())
      )
        return;
      dispatch(setIsSubmitting(true));

      // PRE-FLIGHT VALIDATION: Check signed document requirement BEFORE processing payment
      // This prevents charging customers when booking will fail validation
      // See ticket XGE-2706
      if (requiresSignedDocument && !signedDocumentId) {
        console.error(
          '[BOOKING] Pre-flight validation failed: Missing signed document',
          {
            requiresSignedDocument,
            signedDocumentId,
            tripId: trip?.tripid,
            timestamp: new Date().toISOString()
          }
        );
        ErrorToast(t('errors:booking.requiresSignedDocument'));
        dispatch(setIsSubmitting(false));
        return;
      }

      let preparedPassengers = preparedPassengersRef.current;

      if (!preparedPassengers) {
        const { passengers, uploadEntries } = getPassengersPayload(
          passengerForms,
          bookingplans
        );

        try {
          const stagedUploads = await stageCheckoutPassengerUploads({
            tripId: trip?.tripid,
            passengers,
            uploadEntries,
            uploadFile: uploadCheckoutPassengerFieldFile
          });

          preparedPassengers = stagedUploads.passengers;
        } catch {
          preparedPassengers = applyCheckoutUploadTokens(
            passengers,
            uploadEntries,
            []
          );
        }

        preparedPassengersRef.current = preparedPassengers;
      }

      let paymentIntent = capturedPaymentRef.current?.paymentIntent || {};
      let error = null;
      let status = capturedPaymentRef.current?.status || 'succeeded';
      let paymentType = capturedPaymentRef.current?.paymentMethodType || type;
      const fxTransactionFields =
        capturedPaymentRef.current?.fxTransactionFields ||
        (shouldProcessPayment
          ? buildFxTransactionFields(
              selectedCurrency,
              operatorCurrency,
              booking.amountToCharge || 0
            )
          : {});

      // Only process payment if payment option is not 'optionnopayment'
      if (
        usesManagedIntent &&
        capturedPaymentRef.current?.status === 'processing'
      ) {
        const retrieved = await stripe.retrievePaymentIntent(
          currencyState?.intent?.clientSecret
        );
        if (retrieved.error) throw retrieved.error;
        paymentIntent = retrieved.paymentIntent || {};
        status = paymentIntent.status;
        if (paymentIntent.id !== capturedPaymentRef.current.paymentIntent.id)
          throw new Error('Payment status does not match checkout');
        if (status === SUCCEEDED) {
          capturedPaymentRef.current = {
            ...capturedPaymentRef.current,
            paymentIntent,
            status
          };
          onPaymentRecoveryChange?.(null);
          currencyState.endPayment({ completed: true });
        } else if (['requires_payment_method', 'canceled'].includes(status)) {
          capturedPaymentRef.current = null;
          preparedPassengersRef.current = null;
          paymentFinalizingRef.current = false;
          onPaymentRecoveryChange?.(null);
          currencyState.endPayment({ invalidated: status === 'canceled' });
          ErrorToast(t('errors:bookingPaymentFailed'));
          return;
        } else {
          ErrorToast(t('bookings:currencyPayment.paymentPending'));
          return;
        }
      }
      if (shouldProcessPayment && !capturedPaymentRef.current) {
        paymentFinalizingRef.current = true;
        await paymentIntentUpdatePromiseRef.current.catch(() => {});

        // CRITICAL FIX: Last-looks check - update payment intent with current amountToCharge
        // This ensures the amount we're about to charge matches what the user sees
        const currentAmount = booking.amountToCharge || 0;
        const {
          totalWithFeesInChargeCurrency,
          applicationFeeNotIncluded,
          applicationFeeIncluded
        } = getFeesBreakdown(currentAmount, trip?.fees ?? [], type);

        try {
          if (!hasFxSelection && !currencyState?.managed) {
            const updated = await updatePaymentIntent(paymentIntentId, {
              amount: totalWithFeesInChargeCurrency,
              applicationFees:
                applicationFeeNotIncluded + applicationFeeIncluded
            });
            if (
              updated?.error ||
              updated?.data?.paymentIntentId !== paymentIntentId ||
              !Number.isFinite(Number(updated?.data?.newAmount)) ||
              Math.abs(
                Number(updated.data.newAmount) - totalWithFeesInChargeCurrency
              ) > 0.005
            ) {
              throw new Error('Payment intent amount could not be updated');
            }
          }
        } catch (updateError) {
          console.error(
            '[BOOKING] Last-looks payment intent update failed:',
            updateError
          );
          ErrorToast(t('errors:bookingPaymentFailed'));
          preparedPassengersRef.current = null;
          paymentFinalizingRef.current = false;
          return;
        }

        const paymentResult = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: sanitizeStripeReturnUrl(window.location.href),
            payment_method_data: {
              billing_details: {
                email: customForm?.email.trim(),
                name: `${customForm?.firstName.trim()} ${customForm?.lastName.trim()}`
              }
            }
          },
          redirect: 'if_required' //This instruction avoid Stripe to redirect when getting the transaction result
        });

        error = paymentResult.error;
        paymentIntent = paymentResult.paymentIntent || {};
        status = paymentIntent.status;
        if (
          usesManagedIntent &&
          status === 'requires_action' &&
          stripe.handleNextAction
        ) {
          const action = await stripe.handleNextAction({
            clientSecret: currencyState?.intent?.clientSecret
          });
          error = action.error;
          if (!error) {
            const retrieved = await stripe.retrievePaymentIntent(
              currencyState?.intent?.clientSecret
            );
            error = retrieved.error;
            paymentIntent = retrieved.paymentIntent || {};
            status = paymentIntent.status;
          }
        }
        // FX accounting accepts a final successful charge only. A pending
        // intent is retained for explicit status checks without reconfirming.
        if (usesManagedIntent && status === 'processing') {
          capturedPaymentRef.current = {
            paymentIntent,
            status,
            paymentMethodType: type,
            fxTransactionFields
          };
          onPaymentRecoveryChange?.('processing');
          ErrorToast(t('bookings:currencyPayment.paymentPending'));
          return;
        }

        if (
          paymentIntent &&
          (usesManagedIntent
            ? status === SUCCEEDED
            : successfulStatuses.includes(status))
        ) {
          currencyPaymentCompleted = true;
          capturedPaymentRef.current = {
            paymentIntent,
            status,
            paymentMethodType: type,
            fxTransactionFields
          };
          paymentType = type;
        } else {
          preparedPassengersRef.current = null;
          paymentFinalizingRef.current = false;
        }
      }

      // Proceed with booking creation if payment succeeded or no payment required
      if (
        (shouldProcessPayment &&
          paymentIntent &&
          (usesManagedIntent
            ? status === SUCCEEDED
            : successfulStatuses.includes(status))) ||
        !shouldProcessPayment
      ) {
        await createBookingWithDetails(
          paymentIntent,
          status,
          paymentType,
          preparedPassengers
        );
      } else {
        // Handle payment errors
        if (error) {
          if (error?.type !== 'validation_error') {
            ErrorToast(t('errors:bookingPaymentFailed'));
          }
        }
      }
    } catch (error) {
      if (!capturedPaymentRef.current) {
        preparedPassengersRef.current = null;
        paymentFinalizingRef.current = false;
        ErrorToast(t('errors:bookingUnexpectedError'));
      }
    } finally {
      if (currencyPaymentStarted)
        currencyState.endPayment({
          completed:
            currencyPaymentCompleted ||
            capturedPaymentRef.current?.status === SUCCEEDED,
          unresolved: capturedPaymentRef.current?.status === 'processing'
        });
      dispatch(setIsSubmitting(false));
      submissionInFlightRef.current = false;
    }
  };

  // Extract booking creation logic into reusable function
  const createBookingWithDetails = async (
    paymentIntent,
    status,
    paymentMethodType,
    preparedPassengers
  ) => {
    //Create booking
    const plansForBooking = bookingplans
      .filter((tripPlan) => tripPlan?.selectedQuantity > 0)
      .map((tripPlan) => {
        const foundForms = passengerForms.filter(
          (form) => Number(form?.tripplanid) === Number(tripPlan?.tripplanid)
        );

        return {
          tripplanid: Number(tripPlan?.tripplanid),
          passengers: [],
          occupancy: foundForms?.length ?? 0
        };
      });

    const bookingPayload = capturedPaymentRef.current?.bookingPayload || {
      tripid: trip?.tripid,
      isBookingWithoutPayment: booking?.paymentOption === 'optionnopayment',
      maincontact: {
        email: customForm?.email.trim(),
        firstname: customForm?.firstName.trim(),
        lastname: customForm?.lastName.trim()
      },
      tripplans: plansForBooking,
      payment: {
        amount:
          capturedPaymentRef.current?.fxTransactionFields
            ?.amountappliedtobalance ??
          booking.amountToCharge ??
          0,
        mode:
          booking?.paymentOption === 'optionnopayment' ? 'nopayment' : 'stripe',
        paymentIntentId: paymentIntent?.id,
        status: status,
        paymentMethod: paymentMethodType,
        ...(capturedPaymentRef.current?.fxTransactionFields || {})
      },
      passengers: preparedPassengers || [],
      discountCodes: appliedDiscountCodes.map((discountCode) => ({
        discountCodeId: discountCode.discountCodeId,
        code: discountCode.code
      })),
      termsAcceptance: buildBookingTermsAcceptance({
        accepted: isTermsChecked,
        enabled: booking?.tenant?.tcenabled,
        requiresSignedDocument,
        termsLink: booking?.tenant?.tclink,
        pathname: window.location.pathname
      }),
      signedDocument: signedDocumentId
        ? {
            externalDocumentId: signedDocumentId,
            status: 'pending'
          }
        : null
    };

    if (capturedPaymentRef.current) {
      capturedPaymentRef.current.bookingPayload = bookingPayload;
    }

    const bookingResponse = capturedPaymentRef.current
      ? await completeCapturedBooking({
          complete: () => completeBooking(bookingPayload),
          shouldContinue: () =>
            isMountedRef.current && Boolean(capturedPaymentRef.current)
        })
      : await completeBooking(bookingPayload);
    const { data } = bookingResponse;

    if (data) {
      const bookingid = data?.booking?.bookingid;

      // Track GA purchase conversion event
      const tripId = trip?.tripid;
      const tripName = trip?.tripname;
      const totalprice = Number(data?.totalprice ?? totalPrice);
      const tenant = booking?.tenant || {};
      const currency =
        tenant?.sellingcurrency?.currencycode ||
        tenant?.basecurrency?.currencycode;
      const gaId = tenant?.googleanalyticsid;

      if (gaId && tripId && tripName) {
        initGA(gaId);
        trackEvent('purchase', {
          transaction_id: bookingid,
          value: totalprice,
          currency,
          trip_id: tripId,
          trip_name: tripName
        });
      }

      const bookingResult = {
        maincontact: {
          email: customForm?.email.trim(),
          firstname: customForm?.firstName.trim(),
          lastname: customForm?.lastName.trim()
        },
        ...data,
        droppedDiscountMessages: getDroppedDiscountMessages(
          t,
          data?.droppedDiscountCodes
        ),
        amount: booking.amountToCharge || 0,
        paymentMethod: paymentMethodType
      };

      const shouldProcessPayment =
        !isZeroTotalBooking &&
        booking.paymentOption != 'optionnopayment' &&
        Number(booking.amountToCharge || 0) > 0;
      const message = shouldProcessPayment
        ? status !== SUCCEEDED
          ? t('trip:paymentIsBeingProcessed')
          : t('trip:bookingSuccessful')
        : t('trip:bookingSuccessful'); // No payment bookings are always successful
      const successMessage =
        bookingResult.droppedDiscountMessages?.[0] || message;

      // Update booking result
      dispatch(updateBookingResult({ result: { ...bookingResult } }));
      preparedPassengersRef.current = null;
      capturedPaymentRef.current = null;
      dispatch(updatePassengerForms({ passengerForms: [] }));
      dispatch(resetCheckout());
      dispatch(updateActiveStep({ key: 'success', message: successMessage }));
    } else if (!capturedPaymentRef.current) {
      ErrorToast(t('errors:bookingUnexpectedError'));
    }
  };

  const onChangeCustomInput = (event) => {
    if (capturedPaymentRef.current || paymentFinalizingRef.current) return;
    const { name, value } = event.target;

    dispatch(setCustomForm({ ...customForm, [name]: value }));
    dispatch(setCustomFormError({ ...customFormError, [name]: '' }));
  };

  const onChangePaymentOption = async (evt, value) => {
    if (
      isZeroTotalBooking ||
      !canMutatePaymentIntent({
        capturedPayment: capturedPaymentRef.current,
        paymentFinalizing: paymentFinalizingRef.current
      })
    ) {
      return;
    }

    const optionType = evt?.target?.value || value;
    if (
      optionType === 'optionnopayment' &&
      currencyState?.managed &&
      !(await currencyState.reset())
    )
      return;
    dispatch(setIsProcessing(true));

    // Calculate amount to charge using utility function
    let newAmount = 0;
    let planResult = null;
    if (enableNewPaymentPlans) {
      if (optionType === 'optionnopayment') {
        newAmount = 0;
      } else if (optionType === 'optionfullamount') {
        // Calculate full amount from payment plan to ensure consistency
        const input = buildPaymentPlanInput();
        planResult = input
          ? getBookingPaymentPlan({
              ...input,
              featureFlags: { enableNewPaymentPlans }
            })
          : null;
        newAmount = Number(planResult?.totalPrice ?? discountedTotalPrice);
      } else {
        const input = buildPaymentPlanInput();
        planResult = input
          ? getBookingPaymentPlan({
              ...input,
              featureFlags: { enableNewPaymentPlans }
            })
          : null;
        newAmount = Number(planResult?.dueAtBooking ?? 0);
      }

      if (planResult?.canBook === false) {
        updateBookingTotal(0, [], '');

        dispatch(
          updatePaymentOption({
            paymentOption: 'optionfullamount',
            amountToCharge: 0
          })
        );

        dispatch(setIsProcessing(false));
        return;
      }
    } else {
      newAmount = calculateAmountToCharge(
        optionType,
        discountedTotalPrice,
        totalDeposit
      );
    }

    // If booking without payment or Stripe isn't ready, update UI/redux and exit early
    if (optionType === 'optionnopayment' || !stripe || !elements) {
      // Exclude all fees when no payment
      updateBookingTotal(newAmount ?? 0, [], '');

      dispatch(
        updatePaymentOption({
          paymentOption: optionType,
          amountToCharge: newAmount
        })
      );

      dispatch(setIsProcessing(false));
      return;
    }

    const {
      totalWithFeesInChargeCurrency,
      applicationFeeNotIncluded,
      applicationFeeIncluded
    } = getFeesBreakdown(newAmount ?? 0, trip?.fees ?? [], paymentMethodType);

    updateBookingTotal(newAmount ?? 0, trip?.fees ?? [], paymentMethodType);

    dispatch(
      updatePaymentOption({
        paymentOption: optionType,
        amountToCharge: newAmount
      })
    );

    const result = await updatePaymentIntentWhileMutable(paymentIntentId, {
      amount: totalWithFeesInChargeCurrency,
      applicationFees: applicationFeeNotIncluded + applicationFeeIncluded,
      metadata: {
        'Payment type': enableNewPaymentPlans
          ? // In new logic, map based on whether full equals total
            newAmount >= discountedTotalPrice
            ? t('trip:fullAmount')
            : t('trip:depositAmount')
          : optionType === 'optionfullamount'
            ? t('trip:fullAmount')
            : t('trip:depositAmount')
      }
    });

    if (result?.skipped) {
      dispatch(setIsProcessing(false));
      return;
    }

    if (!result?.data || result?.error) {
      const previousOption =
        optionType === 'optionfullamount'
          ? 'optiondeposit'
          : 'optionfullamount';

      //Rolling back payment option if something went wrong
      const previousAmount = enableNewPaymentPlans
        ? (() => {
            if (previousOption === 'optionnopayment') return 0;
            const input = buildPaymentPlanInput();
            const r = input
              ? getBookingPaymentPlan({
                  ...input,
                  featureFlags: { enableNewPaymentPlans }
                })
              : null;
            return Number(r?.dueAtBooking ?? 0);
          })()
        : calculateAmountToCharge(
            previousOption,
            discountedTotalPrice,
            totalDeposit
          );
      dispatch(
        updatePaymentOption({
          paymentOption: previousOption,
          amountToCharge: previousAmount
        })
      );
    }

    dispatch(setIsProcessing(false));
  };

  const getPassengersPayload = (formData = [], bookingPlans = []) => {
    const passengers = [];
    const uploadEntries = [];

    const sortedForms = [...formData].sort((a, b) => a.order - b.order);

    // Group bookingPlans by tripplanid
    const bookingPlanMap = bookingPlans.reduce((acc, plan) => {
      const { tripplanid } = plan;
      if (!acc[tripplanid]) acc[tripplanid] = [];
      acc[tripplanid].push({ ...plan, assigned: 0 }); // Track how many passengers are assigned
      return acc;
    }, {});

    for (const form of sortedForms) {
      const tripplanid = Number(form?.tripplanid);
      const clientPassengerKey =
        form?.uuid || String(form?.formId || '').trim();
      const mappedFields = form?.fields?.map((field) => {
        const { trippassengerfieldid, key, fieldtype } = field;
        const isFileUploadField = isFileUploadPassengerFieldType(fieldtype);

        if (isFileUploadField) {
          const uploadedFiles = getPassengerFieldUploadFiles(
            passengerFieldFiles,
            form?.formId,
            trippassengerfieldid
          ).filter(isBrowserFile);

          const files = uploadedFiles.map((file, index) => {
            const fileKey = `${form?.formId}:${trippassengerfieldid}:${index + 1}`;

            uploadEntries.push({
              fileKey,
              file,
              tripPassengerFieldId: trippassengerfieldid,
              clientPassengerKey
            });
            return getPassengerFieldUploadMetadata(file, fileKey, index + 1);
          });

          return {
            trippassengerfieldid,
            key,
            options: [],
            files
          };
        }

        const fieldOptions = field?.trippassengerfieldoption ?? [];
        const options = [];
        const hasOptions = fieldtype?.allowoptions;
        const fieldTypeKey = fieldtype?.key;

        if (hasOptions) {
          if (fieldTypeKey === RADIOBUTTON || fieldTypeKey === BOOLEAN) {
            const foundOption = fieldOptions?.find(
              (option) => option?.title === field?.value
            );
            if (foundOption) {
              const { trippassengerfieldoptionid, title: value } = foundOption;
              options.push({ trippassengerfieldoptionid, value });
            }
          } else {
            const values = Array.isArray(field?.value)
              ? field?.value
              : (field?.value?.split(',') ?? []);
            const foundOptions = fieldOptions
              .filter((opt) => values.includes(opt?.title))
              .map(({ trippassengerfieldoptionid, title }) => ({
                trippassengerfieldoptionid,
                value: title
              }));
            options.push(...foundOptions);
          }
        } else {
          options.push({
            trippassengerfieldoptionid: null,
            value: field?.value
          });
        }

        return { trippassengerfieldid, key, options };
      });

      // Assign bookingplanid based on tripplanid and available capacity
      const planGroup = bookingPlanMap[tripplanid] || [];
      let assignedPlanId = null;

      for (const plan of planGroup) {
        if (plan?.assigned < plan?.occupancy) {
          assignedPlanId = plan?.bookingplanid;
          plan.assigned++;
          break;
        }
      }

      // Add the passenger with bookingplanid
      passengers.push([
        ...mappedFields,
        { documentpassengerkey: clientPassengerKey || null },
        { tripplanid },
        { bookingplanid: assignedPlanId },
        {
          tripaddonids: getPassengerTripAddOnIds({
            selectedAddOns,
            passenger: form
          })
        }
      ]);
    }

    return { passengers, uploadEntries };
  };

  const handlePaymentMethodChange = async (event) => {
    if (
      !canMutatePaymentIntent({
        capturedPayment: capturedPaymentRef.current,
        paymentFinalizing: paymentFinalizingRef.current
      })
    ) {
      return;
    }

    if (event?.elementType === 'payment') {
      const currentPaymentMethod = event?.value || {};
      const { type } = currentPaymentMethod;
      if (
        type === 'us_bank_account' &&
        (operatorCurrency !== 'USD' || hasFxSelection)
      ) {
        paymentMethodSelectionRef.current = null;
        dispatch(setIsPaymentMethodComplete(false));
        return;
      }
      const methodChanged =
        type && type !== paymentMethodSelectionRef.current?.type;
      paymentMethodSelectionRef.current = {
        ...currentPaymentMethod,
        complete: Boolean(event?.complete)
      };
      if (methodChanged) {
        // Store the selection before awaiting Stripe. Older responses must not
        // overwrite a newer selection or re-enable submission too early.
        setPreviousPaymentMethod(currentPaymentMethod);
        paymentMethodUpdateCountRef.current += 1;
      }
      dispatch(
        setIsPaymentMethodComplete(
          Boolean(event?.complete) && paymentMethodUpdateCountRef.current === 0
        )
      );
      if (type) onPaymentMethodChange?.(type);

      if (methodChanged) {
        try {
          // Default payment amount to zero
          let currentAmount = 0.0;

          if (enableNewPaymentPlans) {
            if (selectedPaymentOption === 'optionnopayment') {
              currentAmount = 0.0;
            } else if (selectedPaymentOption === 'optionfullamount') {
              const input = buildPaymentPlanInput();
              const result = input
                ? getBookingPaymentPlan({
                    ...input,
                    featureFlags: { enableNewPaymentPlans }
                  })
                : null;
              currentAmount =
                result?.canBook === false
                  ? 0
                  : Number(result?.totalPrice ?? discountedTotalPrice);
            } else {
              const input = buildPaymentPlanInput();
              const result = input
                ? getBookingPaymentPlan({
                    ...input,
                    featureFlags: { enableNewPaymentPlans }
                  })
                : null;
              currentAmount =
                result?.canBook === false
                  ? 0
                  : Number(result?.dueAtBooking ?? 0);
            }
          } else {
            if (selectedPaymentOption === 'optionfullamount') {
              currentAmount = discountedTotalPrice;
            } else if (selectedPaymentOption === 'optiondeposit') {
              currentAmount = totalDeposit;
            } else if (selectedPaymentOption === 'optionnopayment') {
              currentAmount = 0.0;
            }
          }

          const {
            totalWithFeesInChargeCurrency,
            applicationFeeNotIncluded,
            applicationFeeIncluded
          } = getFeesBreakdown(
            currentAmount ?? 0,
            trip?.fees ?? [],
            event?.value?.type ?? ''
          );

          updateBookingTotal(
            currentAmount ?? 0,
            trip?.fees ?? [],
            event?.value?.type ?? ''
          );

          if (selectedPaymentOption === 'optionnopayment') {
            // Cancel the previous payment intent if there was one
            // TODO: Cancel payment intent
          } else if (!usesManagedIntent) {
            // CRITICAL FIX: Await payment intent update when payment method changes
            // This ensures fees are recalculated correctly for the new payment method

            try {
              await updatePaymentIntentWhileMutable(paymentIntentId, {
                amount: totalWithFeesInChargeCurrency,
                applicationFees:
                  applicationFeeNotIncluded + applicationFeeIncluded
              });
            } catch (error) {
              console.error(
                '[BOOKING] Error updating payment intent on method change:',
                error
              );
              // Don't block the UI - last-looks check will catch any issues
            }
          }
        } finally {
          paymentMethodUpdateCountRef.current -= 1;
          if (isMountedRef.current) {
            dispatch(
              setIsPaymentMethodComplete(
                Boolean(paymentMethodSelectionRef.current?.complete) &&
                  paymentMethodUpdateCountRef.current === 0
              )
            );
          }
        }
      }
    }
  };

  return (
    <div id={id} {...rest} data-testid='checkout-form'>
      <form
        id='payment-form'
        className='checkout-payment__form'
        // Only the validated Book button may start checkout, never native form submission.
        onSubmit={(event) => event.preventDefault()}
      >
        {!isZeroTotalBooking && displayDeposit ? (
          <div
            className='item-to-pay'
            style={{
              borderColor:
                selectedPaymentOption === 'optiondeposit' ? primaryColor : ''
            }}
            onClick={() => {
              onChangePaymentOption(null, 'optiondeposit');
            }}
          >
            <div style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}
              >
                <XGRadio
                  id='radio-deposit'
                  name='group-payment-options'
                  label={
                    enableNewPaymentPlans
                      ? hasInterimPayments
                        ? t('trip:depositAndInstallments')
                        : t('trip:deposit')
                      : t('trip:payDeposit')
                  }
                  size='medium'
                  value='optiondeposit'
                  checked={selectedPaymentOption === 'optiondeposit'}
                  onChange={onChangePaymentOption}
                  style={{
                    color:
                      selectedPaymentOption === 'optiondeposit'
                        ? primaryColor
                        : '#0009'
                  }}
                />
                <span className='h3-semibold'>
                  {formatCurrency(
                    enableNewPaymentPlans
                      ? Number(planCalc?.dueAtBooking ?? 0)
                      : totalDeposit
                  )}
                </span>
              </div>
              {enableNewPaymentPlans &&
                selectedPaymentOption === 'optiondeposit' && (
                  <>
                    <div
                      style={{
                        height: '1px',
                        background: 'rgba(0,0,0,0.08)',
                        margin: '8px 0'
                      }}
                    />
                    <div className='deposit-breakdown'>
                      {depositBreakdown.map((item) => {
                        return (
                          <div
                            key={item.key}
                            className='text-sm-regular'
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              color: '#4B5563',
                              marginBottom: '4px'
                            }}
                          >
                            <PaymentStatusIcon status={item.status} />
                            <span>
                              <strong>{item.amount}</strong>
                              {' - '}
                              {item.text}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
            </div>
          </div>
        ) : (
          <></>
        )}

        {!isZeroTotalBooking && (
          <div
            className='item-to-pay'
            style={{
              borderColor:
                selectedPaymentOption === 'optionfullamount' ? primaryColor : ''
            }}
            onClick={() => {
              onChangePaymentOption(null, 'optionfullamount');
            }}
          >
            <div>
              <XGRadio
                id='radio-full-amount'
                name='group-payment-options'
                label='Pay full amount'
                size='medium'
                value='optionfullamount'
                checked={selectedPaymentOption === 'optionfullamount'}
                onChange={onChangePaymentOption}
                style={{
                  color:
                    selectedPaymentOption === 'optionfullamount'
                      ? primaryColor
                      : ''
                }}
              />
            </div>
            <div>
              <span className='h3-semibold'>
                {formatCurrency(fullPaymentAmount)}
              </span>
            </div>
          </div>
        )}

        {!isZeroTotalBooking && allowBookingWithoutPayment ? (
          <div
            className='item-to-pay'
            style={{
              borderColor:
                selectedPaymentOption === 'optionnopayment' ? primaryColor : ''
            }}
            onClick={() => {
              onChangePaymentOption(null, 'optionnopayment');
            }}
          >
            <div className='item-to-pay__content'>
              <XGRadio
                id='radio-nopayment'
                name='group-payment-options'
                label={t('settings:paymentPlan.completeWithoutPayment')}
                size='medium'
                value='optionnopayment'
                checked={selectedPaymentOption === 'optionnopayment'}
                onChange={onChangePaymentOption}
                style={{
                  color:
                    selectedPaymentOption === 'optionnopayment'
                      ? primaryColor
                      : '#0009'
                }}
              />
              {enablePaymentInstructionsFeature &&
                selectedPaymentOption === 'optionnopayment' &&
                paymentInstructionsMarkdown && (
                  <div className='checkout-payment-instructions'>
                    <div className='checkout-payment-instructions__title text-sm-semibold'>
                      {t('settings:paymentPlan.paymentInstructions')}
                    </div>
                    <div className='checkout-payment-instructions__content text-sm-regular'>
                      <Editor
                        id='checkout-payment-instructions-editor'
                        className='checkout-payment-instructions__editor'
                        content={paymentInstructionsMarkdown}
                        editable={false}
                        isPreviewing={true}
                        fontSize={{ xs: '16px', md: '16px' }}
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>
        ) : (
          <></>
        )}

        <div className='checkout-payment__custom-form '>
          <p className='text-md-semibold'>
            {t('trip:primaryContactInformation', 'Primary Contact Information')}
          </p>
          <XGInput
            autoFocus
            error={customFormError.email !== ''}
            hint={customFormError.email}
            label={`${t('common:formLabels.email')}*`}
            name='email'
            placeholder={t('common:formLabels.emailPlaceholder')}
            id='add-day-title-input'
            type='text'
            value={customForm?.email || ''}
            onChange={(evt) => {
              onChangeCustomInput(evt);
            }}
            inputProps={{
              maxLength: 250
            }}
          />

          <div style={{ display: 'flex', gap: '12px', margin: '20px 0' }}>
            <XGInput
              error={customFormError.firstName !== ''}
              hint={customFormError.firstName}
              label={`${t('common:formLabels.firstName')}*`}
              name='firstName'
              placeholder={t('common:formLabels.firstNamePlaceholder')}
              id='add-day-title-input'
              type='text'
              value={customForm?.firstName || ''}
              onChange={(evt) => {
                onChangeCustomInput(evt);
              }}
              inputProps={{
                maxLength: 250
              }}
              style={{ with: '50%' }}
            />
            <XGInput
              error={customFormError.lastName !== ''}
              hint={customFormError.lastName}
              label={`${t('common:formLabels.lastName')}*`}
              name='lastName'
              placeholder={t('common:formLabels.lastNamePlaceholder')}
              id='add-day-title-input'
              type='text'
              value={customForm?.lastName || ''}
              onChange={(evt) => {
                onChangeCustomInput(evt);
              }}
              inputProps={{
                maxLength: 250
              }}
              style={{ with: '50%' }}
            />
          </div>
        </div>

        {!isZeroTotalBooking &&
          selectedPaymentOption != 'optionnopayment' &&
          hasClientSecret && (
            <PaymentElement
              options={{ ...paymentElementOptions, paymentMethodOrder }}
              onChange={handlePaymentMethodChange}
            />
          )}

        {!isZeroTotalBooking &&
          selectedPaymentOption != 'optionnopayment' &&
          hasClientSecret &&
          requiresFullBillingAddress() && (
            <div style={{ marginBottom: '20px' }}>
              <AddressElement
                options={{
                  mode: 'billing',
                  fields: {
                    name: 'never'
                  },
                  defaultValues: {
                    name: `${customForm?.firstName || ''} ${customForm?.lastName || ''}`.trim()
                  }
                }}
              />
            </div>
          )}

        <input
          type='button'
          style={{ display: 'none' }}
          ref={submitRef}
          onClick={handleSubmit}
        />
      </form>
    </div>
  );
};

CheckoutForm.propTypes = {
  id: PropTypes.string.isRequired,
  paymentIntentId: PropTypes.string,
  paymentMethodOrder: PropTypes.array,
  hasClientSecret: PropTypes.bool,
  currencyState: PropTypes.object,
  onPaymentMethodChange: PropTypes.func,
  onPaymentRecoveryChange: PropTypes.func,
  selectedCurrency: PropTypes.shape({
    currency: PropTypes.string,
    chargeAmount: PropTypes.number,
    fxQuoteId: PropTypes.string,
    lockedRate: PropTypes.number,
    expiresAt: PropTypes.number,
    operatorBookingCredit: PropTypes.number
  }),
  passengerFieldFiles: PropTypes.object,
  submitRef: PropTypes.object
};

export default CheckoutForm;
